import React, { useState, useEffect, useRef } from "react";
import { Platform } from "react-native";
import {
  Box,
  VStack,
  HStack,
  Text,
  Center,
  Input,
  Image,
  ScrollView,
  Pressable,
  Spinner,
  Icon,
  useToast,
} from "native-base";
import { LinearGradient } from "expo-linear-gradient";
import { Ionicons } from "@expo/vector-icons";
import { GlobalStyles } from "../../constants/styles";
import { GlobalConstants } from "../../constants/constants";
import { useSelector } from "react-redux";
import axios from "axios";
import Toast from "../Toast";

const buildApiUrl = (baseUrl, port, endpoint) => {
  const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  return `${cleanBaseUrl}:${port}${endpoint}`;
};

const apiTicketUrl = buildApiUrl(
  GlobalConstants.servicesBaseUrl,
  "3001",
  "/api/ticket",
);
const apiPaymentsUrl = buildApiUrl(
  GlobalConstants.servicesBaseUrl,
  "3001",
  "/api/payments",
);

// const _ticketsBase = `${GlobalConstants.ticketsBaseUrl}`.replace(/\/$/, "");
// const apiTicketUrl = `${_ticketsBase}/api/ticket`;
// const apiPaymentsUrl = `${_ticketsBase}/api/payments`;

const figmaShadow =
  Platform.OS === "web"
    ? { boxShadow: "0px 4px 4px 0px rgba(0,0,0,0.25)" }
    : {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 4,
      };

const GradientButton = ({
  title,
  onPress,
  width = 140,
  height = 38,
  isLoading = false,
  disabled = false,
  fullWidth = false,
}) => (
  <Box
    borderRadius={5}
    overflow="hidden"
    borderWidth={1}
    borderColor={GlobalStyles.colors.black}
    w={fullWidth ? "100%" : undefined}
    style={fullWidth ? { height } : { width, height }}
  >
    <LinearGradient
      colors={[
        GlobalStyles.colors.gradientBlueStart || "#000561",
        GlobalStyles.colors.gradientBlueEnd || "#000AC7",
      ]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
      style={{
        flex: fullWidth ? 1 : undefined,
        width: fullWidth ? "100%" : width,
        height,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Pressable
        onPress={onPress}
        disabled={isLoading || disabled}
        style={{
          width: "100%",
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {isLoading ? (
          <Spinner color={GlobalStyles.colors.mustard} size="sm" />
        ) : (
          <Text
            color={GlobalStyles.colors.mustard}
            fontWeight="600"
            fontSize="15px"
            letterSpacing="0.1px"
            fontFamily={GlobalStyles.text.fontFamily}
          >
            {title}
          </Text>
        )}
      </Pressable>
    </LinearGradient>
  </Box>
);

const BuyButton = ({ onPress, isLoading = false, disabled = false }) => (
  <Box
    borderRadius={6}
    overflow="hidden"
    style={{ width: 70, height: 32 }}
    opacity={disabled ? 0.4 : 1}
  >
    <LinearGradient
      colors={
        disabled
          ? ["#AAAAAA", "#888888"]
          : [
              GlobalStyles.colors.gradientBlueStart || "#000561",
              GlobalStyles.colors.gradientBlueEnd || "#000AC7",
            ]
      }
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
      style={{
        width: 70,
        height: 32,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Pressable
        onPress={onPress}
        disabled={isLoading || disabled}
        style={{
          width: "100%",
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {isLoading ? (
          <Spinner color={GlobalStyles.colors.mustard} size="sm" />
        ) : (
          <Text
            color={GlobalStyles.colors.mustard}
            fontWeight="700"
            fontSize="13px"
            fontFamily={GlobalStyles.text.fontFamily}
          >
            Buy
          </Text>
        )}
      </Pressable>
    </LinearGradient>
  </Box>
);

const PlainInput = ({ label, ...props }) => (
  <VStack space={0.5}>
    <Text
      fontSize="11px"
      color={GlobalStyles.colors.black}
      fontFamily={GlobalStyles.text.fontFamily}
    >
      {label}
    </Text>
    <Input
      borderColor="#CCCCCC"
      borderWidth={1}
      borderRadius={4}
      bg={GlobalStyles.colors.white}
      _focus={{ borderColor: GlobalStyles.colors.gradientBlueEnd || "#000AC7" }}
      fontFamily={GlobalStyles.text.fontFamily}
      fontSize="12px"
      py={1.5}
      px={2.5}
      {...props}
    />
  </VStack>
);

const MastercardIcon = () => (
  <Box style={{ width: 38, height: 24, position: "relative" }}>
    <Box
      position="absolute"
      style={{
        left: 0,
        top: 0,
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: "#EB001B",
      }}
    />
    <Box
      position="absolute"
      style={{
        left: 14,
        top: 0,
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: "#F79E1B",
      }}
    />
  </Box>
);

const getTicketStatus = (ticket) => {
  if (!ticket.expiry) return "ACTIVE";
  const expiry = new Date(ticket.expiry);
  const now = new Date();
  if (expiry < now) return "EXPIRED";
  return "ACTIVE";
};

const formatExpiry = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return null;
  }
};

const TicketListScreen = ({ businessName, eventName, onBuy }) => {
  const toast = useToast();
  const user = useSelector((s) => s.user?.user);
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user?.token) {
      axios.defaults.headers.common = { Authorization: `Bearer ${user.token}` };
    }
  }, [user]);

  useEffect(() => {
    if (!user?.token) return;

    const fetchTickets = async () => {
      try {
        setIsLoading(true);

        const apiClient = axios.create({
          headers: { Authorization: `Bearer ${user.token}` },
          timeout: 8000,
        });
        const params = { available: "true" };
        if (businessName) {
          params.company_name = businessName;
        }

        const response = await apiClient.get(apiTicketUrl, { params });
        const raw =
          response.data?.tickets ??
          (Array.isArray(response.data) ? response.data : []);

        setTickets(
          raw.map((t) => ({
            id: t.id,
            name: t.name,
            companyName: t.company_name,
            ticketType: t.ticket_type,
            totalTickets: t.total_tickets,
            totalAvailable: t.total_available,
            value: t.ticket_value,
            valueUSD: t.ticket_value_usd,
            valueLocal: t.ticket_value_local,
            currency: t.currency,
            expiry: t.expiry_date,
            qrCode: t.qr_code,
          })),
        );
      } catch (error) {
        console.error("fetchTickets error:", error);
        toast.show({
          render: () => (
            <Toast
              color={GlobalStyles.colors.red}
              message={
                error?.response?.data?.message ||
                "Unable to load tickets. Check your connection."
              }
            />
          ),
          placement: "top",
          duration: 2500,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchTickets();
  }, [user?.token, businessName]); 

  return (
    <VStack w="100%" space={4}>
      <Center w="100%">
        <VStack w={["92%", "420px", "440px"]} space={3}>
          <Text
            color={GlobalStyles.colors.blue}
            fontWeight="700"
            fontSize={[15, 16, 18]}
            textAlign="center"
            fontFamily={GlobalStyles.text.fontFamily}
            mt={1}
          >
            {businessName || "Business Page"}
          </Text>

          {eventName ? (
            <Text
              color={GlobalStyles.colors.black}
              fontSize={[12, 13, 13]}
              textAlign="center"
              fontFamily={GlobalStyles.text.fontFamily}
              mt={-2}
            >
              {eventName}
            </Text>
          ) : null}

          <Text
            color={GlobalStyles.colors.black}
            fontWeight="700"
            fontSize={[12, 13, 14]}
            fontFamily={GlobalStyles.text.fontFamily}
          >
            Available Tickets
          </Text>

          <Box
            bg={GlobalStyles.colors.white}
            borderRadius={12}
            px={5}
            py={2}
            style={figmaShadow}
          >
            {isLoading ? (
              <Center py={8}>
                <Spinner color={GlobalStyles.colors.blue} size="lg" />
                <Text
                  mt={2}
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize="12px"
                >
                  Loading tickets…
                </Text>
              </Center>
            ) : tickets.length === 0 ? (
              <Center py={6}>
                <Text
                  color="gray.500"
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize="12px"
                >
                  No tickets available for {businessName || "this business"}.
                </Text>
              </Center>
            ) : (
              <VStack space={0}>
                {tickets.map((ticket) => {
                  const status = getTicketStatus(ticket);
                  const isExpired = status === "EXPIRED";
                  const expiryText = formatExpiry(ticket.expiry);
                  const isUnavailable =
                    isExpired || ticket.totalAvailable === 0;

                  return (
                    <HStack
                      key={ticket.id}
                      alignItems="center"
                      justifyContent="space-between"
                      py={3}
                    >
                      <VStack flex={1} mr={3} space={0.5}>
                        <Text
                          color={isExpired ? "#999" : GlobalStyles.colors.black}
                          fontSize="14px"
                          fontFamily={GlobalStyles.text.fontFamily}
                        >
                          {ticket.name}
                        </Text>
                        <HStack space={2} alignItems="center" flexWrap="wrap">
                          <Box
                            px={1.5}
                            py={0.5}
                            borderRadius={4}
                            bg={isExpired ? "#FFEEEE" : "#EEFFEE"}
                          >
                            <Text
                              fontSize="10px"
                              fontWeight="600"
                              color={isExpired ? "#CC0000" : "#006600"}
                              fontFamily={GlobalStyles.text.fontFamily}
                            >
                              {isExpired ? "EXPIRED" : "ACTIVE"}
                            </Text>
                          </Box>
                          {!isExpired && ticket.totalAvailable != null && (
                            <Text
                              fontSize="10px"
                              color="#888"
                              fontFamily={GlobalStyles.text.fontFamily}
                            >
                              {ticket.totalAvailable} left
                            </Text>
                          )}
                          {expiryText && (
                            <Text
                              fontSize="10px"
                              color="#AAA"
                              fontFamily={GlobalStyles.text.fontFamily}
                            >
                              Exp: {expiryText}
                            </Text>
                          )}
                          {ticket.valueUSD || ticket.value ? (
                            <Text
                              fontSize="10px"
                              color="#666"
                              fontFamily={GlobalStyles.text.fontFamily}
                            >
                              $
                              {parseFloat(
                                ticket.valueUSD || ticket.value || 0,
                              ).toFixed(2)}
                            </Text>
                          ) : null}
                        </HStack>
                      </VStack>
                      <BuyButton
                        onPress={() => onBuy(ticket)}
                        disabled={isUnavailable}
                      />
                    </HStack>
                  );
                })}
              </VStack>
            )}
          </Box>
        </VStack>
      </Center>
    </VStack>
  );
};

const PaymentScreen = ({ ticket, onSuccess, onFailure, onBack }) => {
  const toast = useToast();
  const user = useSelector((s) => s.user?.user);

  const [cardType, setCardType] = useState("mastercard");
  const [cardHolder, setCardHolder] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user?.token) {
      axios.defaults.headers.common = { Authorization: `Bearer ${user.token}` };
    }
  }, [user]);

  const baseUSD = parseFloat(ticket?.valueUSD ?? ticket?.value ?? 0);
  const tax = parseFloat((baseUSD * 0.2).toFixed(2));
  const total = parseFloat((baseUSD + tax).toFixed(2));

  const showErr = (msg) =>
    toast.show({
      render: () => <Toast color={GlobalStyles.colors.red} message={msg} />,
      placement: "top",
      duration: 2000,
    });

  const handleCardNumber = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 16);
    setCardNumber(d.replace(/(.{4})/g, "$1 ").trim());
  };

  const handleExpiry = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 6);
    setExpiry(d.length <= 2 ? d : `${d.slice(0, 2)}/${d.slice(2)}`);
  };

  const handleConfirm = async () => {
    if (!cardHolder.trim()) return showErr("Enter card holder name");
    if (cardNumber.replace(/\s/g, "").length < 16)
      return showErr("Enter a valid 16-digit card number");
    if (!expiry || expiry.length < 7) return showErr("Enter expiry as MM/YYYY");
    if (!cvv || cvv.length < 3) return showErr("Enter a valid CVV");

    setIsLoading(true);

    try {
      const headers = {
        Authorization: `Bearer ${user?.token}`,
        "Content-Type": "application/json",
      };

      let payment_intent_id = null;
      try {
        const intentBody = { ticket_id: ticket.id };
        if (email.trim()) intentBody.email = email.trim();

        const intentRes = await axios.post(
          `${apiPaymentsUrl}/create-payment-intent`,
          intentBody,
          { headers, timeout: 15000 },
        );
        payment_intent_id = intentRes.data?.payment_intent_id || null;
        console.log("Payment intent created:", payment_intent_id);
      } catch (intentErr) {
        console.warn(
          "create-payment-intent skipped:",
          intentErr?.response?.status,
          intentErr?.response?.data,
        );
      }

      if (payment_intent_id) {
        try {
          await axios.post(
            `${apiPaymentsUrl}/confirm-payment`,
            { payment_intent_id, ticket_id: ticket.id },
            { headers, timeout: 15000 },
          );
          console.log("Payment intent confirmed");
        } catch (confirmErr) {
          console.warn(
            "confirm-payment skipped:",
            confirmErr?.response?.status,
            confirmErr?.response?.data,
          );
        }
      }

      console.log("Calling buy API for ticket:", ticket.id);
      await axios.post(
        `${apiTicketUrl}/${ticket.id}/buy`,
        {},
        { headers, timeout: 15000 },
      );
      console.log("Buy API success for ticket:", ticket.id);

      let ticketData = null;
      try {
        const ticketRes = await axios.get(`${apiTicketUrl}/${ticket.id}`, {
          headers,
          timeout: 10000,
        });
        ticketData = ticketRes.data?.ticket ?? ticketRes.data;
        console.log(
          "Ticket detail fetched, qr_code present:",
          !!ticketData?.qr_code,
        );
      } catch (tErr) {
        console.warn("GET ticket detail skipped:", tErr?.response?.data);
      }

      let statusData = null;
      try {
        const statusRes = await axios.get(
          `${apiTicketUrl}/${ticket.id}/status`,
          { headers, timeout: 10000 },
        );
        statusData = statusRes.data;
        console.log("Ticket status:", statusData?.status);
      } catch (sErr) {
        console.warn("GET ticket status skipped:", sErr?.response?.data);
      }

      onSuccess({
        ticket: { ...ticket, qrCode: ticketData?.qr_code || ticket.qrCode },
        ticketDetail: ticketData,
        status: statusData,
        paymentIntentId: payment_intent_id,
      });
    } catch (error) {
      console.error("Buy ticket FAILED:", error);
      console.error(
        "Buy error response:",
        error?.response?.status,
        error?.response?.data,
      );

      let msg = "Purchase failed. Please try again.";
      if (error?.response?.data?.message) msg = error.response.data.message;
      else if (error?.response?.data?.error) msg = error.response.data.error;
      else if (error?.code === "ECONNABORTED")
        msg = "Server not responding. Try again.";
      else if (error?.response?.status === 400)
        msg = "Ticket not available or already purchased.";
      else if (error?.response?.status === 401)
        msg = "Session expired. Please log in again.";
      else if (error?.response?.status === 404) msg = "Ticket not found.";

      toast.show({
        render: () => <Toast color={GlobalStyles.colors.red} message={msg} />,
        placement: "top",
        duration: 3000,
      });
      onFailure();
    } finally {
      setIsLoading(false);
    }
  };

  const RadioDot = ({ selected }) => (
    <Box
      w={15}
      h={15}
      borderRadius={8}
      borderWidth={2}
      borderColor={
        selected ? GlobalStyles.colors.gradientBlueEnd || "#000AC7" : "#AAAAAA"
      }
      alignItems="center"
      justifyContent="center"
    >
      {selected && (
        <Box
          w={6}
          h={6}
          borderRadius={3}
          bg={GlobalStyles.colors.gradientBlueEnd || "#000AC7"}
        />
      )}
    </Box>
  );

  return (
    <VStack w="100%" space={0}>
      <Center w="100%">
        <Box w={["92%", "560px", "580px"]}>
          <Text
            color={GlobalStyles.colors.blue}
            fontWeight="700"
            fontSize={[14, 15, 16]}
            textAlign="center"
            fontFamily={GlobalStyles.text.fontFamily}
            mb={4}
          >
            {ticket?.name}
          </Text>

          <HStack alignItems="flex-start" space={[3, 5, 6]} flexWrap="wrap">
            <VStack flex={1} minW="220px" space={2.5} mb={4}>
              <HStack alignItems="center" space={1.5} mb={0.5}>
                <Icon
                  as={Ionicons}
                  name="card-outline"
                  size={4}
                  color={GlobalStyles.colors.black}
                />
                <Text
                  fontWeight="600"
                  fontSize="13px"
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Payment
                </Text>
              </HStack>

              <VStack space={1}>
                <Text
                  fontSize="11px"
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Select card type
                </Text>
                <HStack space={5} alignItems="center">
                  <Pressable onPress={() => setCardType("visa")}>
                    <HStack space={1.5} alignItems="center">
                      <RadioDot selected={cardType === "visa"} />
                      <Box
                        borderWidth={1}
                        borderColor="#CCCCCC"
                        borderRadius={3}
                        px={2}
                        py={0.5}
                        bg={GlobalStyles.colors.white}
                      >
                        <Text
                          fontWeight="900"
                          fontSize="12px"
                          color="#1A1F71"
                          fontFamily={GlobalStyles.text.fontFamily}
                          letterSpacing="1px"
                        >
                          VISA
                        </Text>
                      </Box>
                    </HStack>
                  </Pressable>

                  <Pressable onPress={() => setCardType("mastercard")}>
                    <HStack space={1.5} alignItems="center">
                      <RadioDot selected={cardType === "mastercard"} />
                      <MastercardIcon />
                    </HStack>
                  </Pressable>
                </HStack>
              </VStack>

              <PlainInput
                label="Card holder's name"
                value={cardHolder}
                onChangeText={setCardHolder}
                placeholder="Name on card"
              />

              <PlainInput
                label="Card number"
                value={cardNumber}
                onChangeText={handleCardNumber}
                placeholder="---- ---- ---- ----"
                keyboardType="numeric"
                InputRightElement={
                  <Box mr={2}>
                    <Icon
                      as={Ionicons}
                      name="card-outline"
                      size="xs"
                      color="gray.400"
                    />
                  </Box>
                }
              />

              <HStack space={2}>
                <Box flex={1}>
                  <PlainInput
                    label="Expiry date"
                    value={expiry}
                    onChangeText={handleExpiry}
                    placeholder="MM/YYYY"
                    keyboardType="numeric"
                  />
                </Box>
                <Box w="90px">
                  <PlainInput
                    label="CVV"
                    value={cvv}
                    onChangeText={(v) =>
                      setCvv(v.replace(/\D/g, "").slice(0, 4))
                    }
                    placeholder="Code"
                    keyboardType="numeric"
                    secureTextEntry
                  />
                </Box>
              </HStack>

              <PlainInput
                label="Enter Email"
                value={email}
                onChangeText={setEmail}
                placeholder="If different than Username"
                keyboardType="email-address"
                autoCapitalize="none"
              />

              <Pressable onPress={onBack} mt={0.5}>
                <Text
                  color={GlobalStyles.colors.blue}
                  fontSize="11px"
                  underline
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  ← Back to tickets
                </Text>
              </Pressable>
            </VStack>

            <VStack w={["100%", "190px", "200px"]} space={1.5}>
              <Text
                fontWeight="600"
                fontSize="13px"
                color={GlobalStyles.colors.black}
                fontFamily={GlobalStyles.text.fontFamily}
              >
                Payment Summary
              </Text>

              <Box borderRadius={12} overflow="hidden" style={figmaShadow}>
                <Box
                  bg={GlobalStyles.colors.white}
                  px={3}
                  py={4}
                  alignItems="center"
                >
                  <Box
                    w={44}
                    h={44}
                    bg="#EEF0FF"
                    borderRadius={10}
                    alignItems="center"
                    justifyContent="center"
                    mb={2.5}
                  >
                    <Icon
                      as={Ionicons}
                      name="receipt-outline"
                      size={6}
                      color={GlobalStyles.colors.gradientBlueEnd || "#000AC7"}
                    />
                  </Box>
                  <Text
                    color={GlobalStyles.colors.blue}
                    fontWeight="700"
                    fontSize="12px"
                    fontFamily={GlobalStyles.text.fontFamily}
                    textAlign="center"
                    numberOfLines={2}
                  >
                    {ticket?.name}
                  </Text>
                </Box>

                <Box
                  bg={GlobalStyles.colors.mustard || "#F5C518"}
                  px={3}
                  py={3}
                >
                  <HStack justifyContent="space-between" mb={2}>
                    <Text
                      fontSize="12px"
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      Base:
                    </Text>
                    <Text
                      fontSize="12px"
                      fontWeight="500"
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      {baseUSD} USD
                    </Text>
                  </HStack>
                  <HStack justifyContent="space-between" mb={3}>
                    <Text
                      fontSize="12px"
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      Tax:
                    </Text>
                    <Text
                      fontSize="12px"
                      fontWeight="500"
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      {tax} USD
                    </Text>
                  </HStack>
                  <Box
                    w="100%"
                    h="1px"
                    bg={GlobalStyles.colors.black}
                    opacity={0.2}
                    mb={3}
                  />
                  <HStack justifyContent="space-between" mb={4}>
                    <Text
                      fontSize="13px"
                      fontWeight="700"
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      Total:
                    </Text>
                    <Text
                      fontSize="13px"
                      fontWeight="700"
                      color={GlobalStyles.colors.black}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      {total} USD
                    </Text>
                  </HStack>
                  <GradientButton
                    title="Confirm"
                    onPress={handleConfirm}
                    height={38}
                    isLoading={isLoading}
                    fullWidth
                  />
                </Box>
              </Box>
            </VStack>
          </HStack>
        </Box>
      </Center>
    </VStack>
  );
};

const PaymentSuccessScreen = ({ result, onDone, onSaveTicket }) => {
  const { ticket, status, ticketDetail } = result || {};
  const qrSource = ticketDetail?.qr_code || ticket?.qrCode || null;

  const expiryFormatted = (() => {
    const d = status?.expiry_date || ticket?.expiry;
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
    });
  })();

  return (
    <VStack w="100%" space={4}>
      <Center w="100%">
        <VStack w={["92%", "360px", "380px"]} space={4} alignItems="center">
          <Text
            color={GlobalStyles.colors.blue}
            fontWeight="700"
            fontSize={[15, 17, 18]}
            textAlign="center"
            fontFamily={GlobalStyles.text.fontFamily}
          >
            Payment Successful
          </Text>

          <Box
            w="100%"
            bg={GlobalStyles.colors.white}
            borderRadius={12}
            px={5}
            py={5}
            alignItems="center"
            style={figmaShadow}
          >
            <Text
              color={GlobalStyles.colors.black}
              fontSize="12px"
              fontFamily={GlobalStyles.text.fontFamily}
              mb={4}
              textAlign="center"
            >
              Take picture or save your QR code ticket
            </Text>

            <Box
              w={150}
              h={150}
              bg={GlobalStyles.colors.black}
              borderRadius={4}
              alignItems="center"
              justifyContent="center"
              mb={4}
            >
              {qrSource ? (
                <Image
                  source={{ uri: qrSource }}
                  alt="QR Code"
                  style={{ width: "92%", height: "92%" }}
                  resizeMode="contain"
                />
              ) : (
                <Box
                  w="88%"
                  h="88%"
                  bg={GlobalStyles.colors.white}
                  borderRadius={2}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon
                    as={Ionicons}
                    name="qr-code-outline"
                    size={10}
                    color={GlobalStyles.colors.black}
                  />
                </Box>
              )}
            </Box>

            <VStack w="100%" space={2.5}>
              <HStack justifyContent="space-between">
                <Text
                  color={GlobalStyles.colors.black}
                  fontSize="12px"
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Your Ticket ID
                </Text>
                <Text
                  color={GlobalStyles.colors.black}
                  fontSize="12px"
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  {ticket?.id ?? status?.ticket_id ?? "—"}
                </Text>
              </HStack>
              <HStack justifyContent="space-between">
                <Text
                  color={GlobalStyles.colors.black}
                  fontSize="12px"
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Ticket Company:
                </Text>
                <Text
                  color={GlobalStyles.colors.black}
                  fontSize="12px"
                  fontFamily={GlobalStyles.text.fontFamily}
                  numberOfLines={1}
                  maxW="55%"
                >
                  {ticket?.companyName ?? status?.company_name ?? "—"}
                </Text>
              </HStack>
              <HStack justifyContent="space-between">
                <Text
                  color={GlobalStyles.colors.black}
                  fontSize="12px"
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Ticket Expiry:
                </Text>
                <Text
                  color={GlobalStyles.colors.black}
                  fontSize="12px"
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  {expiryFormatted}
                </Text>
              </HStack>
              <HStack justifyContent="space-between">
                <Text
                  color={GlobalStyles.colors.black}
                  fontSize="12px"
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Status:
                </Text>
                <Box px={1.5} py={0.5} borderRadius={4} bg="#EEFFEE">
                  <Text
                    fontSize="11px"
                    fontWeight="600"
                    color="#006600"
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    {status?.status || "ACTIVE"}
                  </Text>
                </Box>
              </HStack>
            </VStack>

            <Text
              color={GlobalStyles.colors.black}
              fontSize="11px"
              fontFamily={GlobalStyles.text.fontFamily}
              mt={4}
              textAlign="center"
              opacity={0.6}
            >
              Ticket Details Sent on Email
            </Text>
          </Box>

          <HStack space={3} mt={1}>
            <GradientButton
              title="Save Ticket"
              onPress={onSaveTicket}
              width={110}
              height={34}
            />
            <Pressable
              onPress={onDone}
              borderRadius={5}
              borderWidth={1}
              borderColor={GlobalStyles.colors.blue}
              px={4}
              h={34}
              justifyContent="center"
              alignItems="center"
            >
              <Text
                color={GlobalStyles.colors.blue}
                fontWeight="600"
                fontSize="13px"
                fontFamily={GlobalStyles.text.fontFamily}
              >
                Done
              </Text>
            </Pressable>
          </HStack>
        </VStack>
      </Center>
    </VStack>
  );
};

const SaveTicketScreen = ({ onGetStarted, onBack }) => (
  <VStack w="100%" space={4}>
    <Center w="100%">
      <VStack w={["92%", "320px", "340px"]} space={4} alignItems="center">
        <Text
          color={GlobalStyles.colors.blue}
          fontWeight="700"
          fontSize={[15, 17, 18]}
          textAlign="center"
          fontFamily={GlobalStyles.text.fontFamily}
        >
          Save Your Ticket
        </Text>

        <Box
          w="100%"
          h={200}
          borderRadius={14}
          overflow="hidden"
          style={figmaShadow}
        >
          <LinearGradient
            colors={["#1B1FCE", "#0A0D8F"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{ flex: 1, padding: 16 }}
          >
            <Box position="absolute" top={12} left={12} opacity={0.65}>
              <Icon
                as={Ionicons}
                name="ellipse-outline"
                size={5}
                color={GlobalStyles.colors.mustard || "#F5C518"}
              />
            </Box>
            <Box position="absolute" top={24} right={60}>
              <Icon as={Ionicons} name="star" size={3} color="white" />
            </Box>
            <Box position="absolute" top={56} left={48}>
              <Icon as={Ionicons} name="star" size={3} color="white" />
            </Box>
            <Box position="absolute" bottom={38} right={14}>
              <Icon
                as={Ionicons}
                name="star"
                size={4}
                color={GlobalStyles.colors.mustard || "#F5C518"}
              />
            </Box>
            <Box
              position="absolute"
              bottom={12}
              right={8}
              w={30}
              h={30}
              borderRadius={15}
              bg="rgba(255,255,255,0.12)"
            />
            <Box
              position="absolute"
              top={14}
              right={-10}
              w={46}
              h={46}
              borderRadius={23}
              bg="rgba(255,255,255,0.09)"
            />
            <Box
              position="absolute"
              bottom={22}
              left={-8}
              w={38}
              h={38}
              borderRadius={19}
              bg={GlobalStyles.colors.mustard || "#F5C518"}
              opacity={0.22}
            />
            <Center flex={1}>
              <Box
                w={54}
                h={54}
                borderRadius={27}
                bg="rgba(255,255,255,0.15)"
                alignItems="center"
                justifyContent="center"
                mb={2.5}
              >
                <Icon
                  as={Ionicons}
                  name="location"
                  size={6}
                  color={GlobalStyles.colors.mustard || "#F5C518"}
                />
              </Box>
              <Text
                color="white"
                fontWeight="600"
                fontSize="12px"
                textAlign="center"
                fontFamily={GlobalStyles.text.fontFamily}
                mb={3.5}
              >
                Open location to save Ticket
              </Text>
              <Pressable
                onPress={onGetStarted}
                borderWidth={1}
                borderColor="white"
                borderRadius={6}
                px={5}
                py={1}
              >
                <Text
                  color="white"
                  fontWeight="600"
                  fontSize="12px"
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Get Started
                </Text>
              </Pressable>
            </Center>
          </LinearGradient>
        </Box>

        <Pressable onPress={onBack}>
          <Text
            color={GlobalStyles.colors.blue}
            fontSize="12px"
            underline
            fontFamily={GlobalStyles.text.fontFamily}
          >
            ← Back
          </Text>
        </Pressable>
      </VStack>
    </Center>
  </VStack>
);

const PaymentFailedScreen = ({ onTryAgain }) => (
  <VStack w="100%" space={4}>
    <Center w="100%">
      <Box w={["92%", "260px", "280px"]} pt={7}>
        <Box position="relative">
          <Box position="absolute" top={-25} alignSelf="center" zIndex={10}>
            <Box
              w={50}
              h={50}
              borderRadius={25}
              bg={GlobalStyles.colors.gradientBlueEnd || "#000AC7"}
              alignItems="center"
              justifyContent="center"
              style={
                Platform.OS === "web"
                  ? { boxShadow: "0px 4px 8px rgba(0,5,200,0.3)" }
                  : {}
              }
            >
              <Icon as={Ionicons} name="close" size={5} color="white" />
            </Box>
          </Box>
          <Box
            w="100%"
            bg={GlobalStyles.colors.white}
            borderRadius={8}
            px={5}
            pt={8}
            pb={5}
            alignItems="center"
            style={figmaShadow}
          >
            <Text
              color={GlobalStyles.colors.black}
              fontWeight="700"
              fontSize={[15, 16, 16]}
              fontFamily={GlobalStyles.text.fontFamily}
              mb={1.5}
              textAlign="center"
            >
              Oops!
            </Text>
            <Text
              color={GlobalStyles.colors.black}
              fontSize="12px"
              fontFamily={GlobalStyles.text.fontFamily}
              mb={5}
              textAlign="center"
              opacity={0.7}
            >
              Payment Not Successful
            </Text>
            <GradientButton
              title="Try Again"
              onPress={onTryAgain}
              width={120}
              height={36}
            />
          </Box>
        </Box>
      </Box>
    </Center>
  </VStack>
);

const CitizenTicketManager = ({
  onBack,
  businessName,
  businessId,
  eventName,
}) => {
  const scrollRef = useRef(null);
  const [screen, setScreen] = useState("list");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);

  const scrollTop = () => scrollRef.current?.scrollTo({ y: 0, animated: true });
  const go = (s) => {
    setScreen(s);
    scrollTop();
  };

  return (
    <ScrollView ref={scrollRef} flex={1}>
      <Center>
        <VStack w="100%" py={5} space={4}>
          {onBack && screen === "list" && (
            <Box w={["92%", "440px", "440px"]} alignSelf="center">
              <Pressable onPress={onBack}>
                <Text
                  color={GlobalStyles.colors.blue}
                  fontWeight="600"
                  fontSize={[12, 13, 14]}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  ← Back to Services
                </Text>
              </Pressable>
            </Box>
          )}

          {screen === "list" && (
            <TicketListScreen
              businessName={businessName} 
              businessId={businessId} 
              eventName={eventName}
              onBuy={(ticket) => {
                setSelectedTicket(ticket);
                go("payment");
              }}
            />
          )}

          {screen === "payment" && selectedTicket && (
            <PaymentScreen
              ticket={selectedTicket}
              onSuccess={(result) => {
                setPaymentResult(result);
                go("success");
              }}
              onFailure={() => go("failed")}
              onBack={() => {
                setSelectedTicket(null);
                go("list");
              }}
            />
          )}

          {screen === "success" && paymentResult && (
            <PaymentSuccessScreen
              result={paymentResult}
              onDone={() => {
                setPaymentResult(null);
                setSelectedTicket(null);
                go("list");
              }}
              onSaveTicket={() => go("save")}
            />
          )}

          {screen === "save" && (
            <SaveTicketScreen
              onGetStarted={() => {
                setPaymentResult(null);
                setSelectedTicket(null);
                go("list");
              }}
              onBack={() => go("success")}
            />
          )}

          {screen === "failed" && (
            <PaymentFailedScreen onTryAgain={() => go("payment")} />
          )}
        </VStack>
      </Center>
    </ScrollView>
  );
};

export {
  CitizenTicketManager,
  TicketListScreen,
  PaymentScreen,
  PaymentSuccessScreen,
  SaveTicketScreen,
  PaymentFailedScreen,
};
export default CitizenTicketManager;
